import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Constants for better maintainability
const GLASS_SHADOW_LIGHT =
    'shadow-[0_0_6px_rgba(0,0,0,0.03),0_2px_6px_rgba(0,0,0,0.08),inset_3px_3px_0.5px_-3px_rgba(0,0,0,0.9),inset_-3px_-3px_0.5px_-3px_rgba(0,0,0,0.85),inset_1px_1px_1px_-0.5px_rgba(0,0,0,0.6),inset_-1px_-1px_1px_-0.5px_rgba(0,0,0,0.6),inset_0_0_6px_6px_rgba(0,0,0,0.12),inset_0_0_2px_2px_rgba(0,0,0,0.06),0_0_12px_rgba(255,255,255,0.15)]';

const GLASS_SHADOW_DARK =
    'dark:shadow-[0_0_8px_rgba(0,0,0,0.03),0_2px_6px_rgba(0,0,0,0.08),inset_3px_3px_0.5px_-3.5px_rgba(255,255,255,0.09),inset_-3px_-3px_0.5px_-3.5px_rgba(255,255,255,0.85),inset_1px_1px_1px_-0.5px_rgba(255,255,255,0.6),inset_-1px_-1px_1px_-0.5px_rgba(255,255,255,0.6),inset_0_0_6px_6px_rgba(255,255,255,0.12),inset_0_0_2px_2px_rgba(255,255,255,0.06),0_0_12px_rgba(0,0,0,0.15)]';

const GLASS_SHADOW = `${GLASS_SHADOW_LIGHT} ${GLASS_SHADOW_DARK}`;

const DEFAULT_GLASS_FILTER_SCALE = 30;

// Shared glass filter component
interface GlassFilterProps {
    id: string;
    scale?: number;
}

const GlassFilter = React.memo(({ id, scale = DEFAULT_GLASS_FILTER_SCALE }: GlassFilterProps) => (
    <svg className="hidden">
        <title>Glass Effect Filter</title>
        <defs>
            <filter
                colorInterpolationFilters="sRGB"
                height="200%"
                id={id}
                width="200%"
                x="-50%"
                y="-50%"
            >
                <feTurbulence
                    baseFrequency="0.05 0.05"
                    numOctaves="1"
                    result="turbulence"
                    seed="1"
                    type="fractalNoise"
                />
                <feGaussianBlur in="turbulence" result="blurredNoise" stdDeviation="2" />
                <feDisplacementMap
                    in="SourceGraphic"
                    in2="blurredNoise"
                    result="displaced"
                    scale={scale}
                    xChannelSelector="R"
                    yChannelSelector="B"
                />
                <feGaussianBlur in="displaced" result="finalBlur" stdDeviation="4" />
                <feComposite in="finalBlur" in2="finalBlur" operator="over" />
            </filter>
        </defs>
    </svg>
));
GlassFilter.displayName = 'GlassFilter';

// Liquid Glass Card - extends shadcn Card with glass effect
const liquidGlassCardVariants = cva(
    'group relative bg-background/20 backdrop-blur-[2px] transition-all duration-300',
    {
        variants: {
            glassSize: {
                sm: 'p-4',
                default: 'p-6',
                lg: 'p-8',
            },
        },
        defaultVariants: {
            glassSize: 'default',
        },
    }
);

export type LiquidGlassCardProps = React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof liquidGlassCardVariants> & {
        glassEffect?: boolean;
    };

function LiquidGlassCard({
    className,
    glassSize,
    glassEffect = true,
    children,
    ...props
}: LiquidGlassCardProps) {
    const filterId = React.useId();

    return (
        <Card className={cn(liquidGlassCardVariants({ glassSize }), className)} {...props}>
            <div
                className={cn(
                    'pointer-events-none absolute inset-0 rounded-xl transition-all',
                    GLASS_SHADOW
                )}
            />

            {glassEffect && (
                <>
                    <div
                        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-lg"
                        style={{ backdropFilter: `url("#${filterId}")` }}
                    />
                    <GlassFilter id={filterId} scale={DEFAULT_GLASS_FILTER_SCALE} />
                </>
            )}

            <div className="relative z-10 flex flex-col h-full">{children}</div>

            <div className="pointer-events-none absolute inset-0 z-20 rounded-lg bg-gradient-to-r from-transparent via-black/5 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 dark:via-white/5" />
        </Card>
    );
}

export { LiquidGlassCard, GlassFilter };
